import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { isBefore } from 'date-fns';
import jwt_decode from 'jwt-decode';
import {
  Observable,
  catchError,
  filter,
  fromEvent,
  map,
  of,
  tap,
  throwError,
} from 'rxjs';

import {
  AuthConfig,
  AuthDetails,
  OtpSession,
  ResetPasswordBody,
  Session,
  SignUpDetails,
  TwoFactorAuthDetails,
} from '@lockton-apps/shared/interfaces';

import { AUTH_CONFIG } from '../auth-config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  model = 'authentication';

  private _callingUrl: string | null = null;

  get callingUrl() {
    return this._callingUrl;
  }
  set callingUrl(value: string | null) {
    this._callingUrl = value;
  }
  constructor(
    @Inject('BASE_API_URL') private baseUrl: string,
    @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
    private http: HttpClient,
    private router: Router,
    private ngZone: NgZone
  ) {}

  init() {
    const session = this.checkSessionValid();
    return session;
    // this.refreshToken();
  }

  authenticate(auth: AuthDetails) {
    const body = {
      email: auth.email,
      password: auth.password,
    };

    return this.http.post(this._getUrl(), body, { responseType: 'text' }).pipe(
      map((token) => {
        this._setTokenAndSession(token);
        const otpSession = jwt_decode<OtpSession>(token);
        if (otpSession?.requiresMfa) {
          return otpSession;
        } else {
          return this._createSession(token);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({ ...error, error: JSON.parse(error.error) }));
      })
    );
  }

  twoFactorAuthenticate(twoFactorDetails: TwoFactorAuthDetails) {
    return this.http
      .put(
        this._get2faUrl(),
        { otp: twoFactorDetails.twoFactorCode },
        { responseType: 'text' }
      )
      .pipe(
        map((token) => {
          this._setTokenAndSession(token);
          return this._createSession(token);
        })
      );
  }

  resend2faCode() {
    return this.http.put(this._getResend2faUrl(), null);
  }

  refreshToken() {
    return this.http.get(this._getUrl(), { responseType: 'text' }).pipe(
      tap((token) => {
        this._setTokenAndSession(token);
      })
    );
  }

  signUp(details: SignUpDetails): Observable<Session> {
    return this.http
      .post(`${this.baseUrl}/${this.model}/sign-up`, details, {
        responseType: 'text',
      })
      .pipe(
        map((token) => {
          this._setTokenAndSession(token);
          return this._createSession(token);
        })
      );
  }

  forgotPassword(email: string) {
    return this.http.put(this._getForgotPasswordUrl(), { email });
  }

  resetPassword(resetPasswordBody: ResetPasswordBody) {
    const body = {
      ...resetPasswordBody,
    };
    return this.http.put(this._getResetPasswordUrl(), body);
  }

  //todo - can this be deleted?
  setPassword(resetPasswordBody: ResetPasswordBody) {
    const body = {
      ...resetPasswordBody,
    };
    return this.http.put(this._getResetPasswordUrl(), body);
  }

  changePassword(userId: string, password: string) {
    return this.http.put(this._getChangePasswordUrl(), { userId, password });
  }

  logout(): Observable<void> {
    const session = this.checkSessionValid();

    if (!session) {
      this.handleLogout();
      return of(undefined);
    }

    return this.http.delete<void>(this._getUrl()).pipe(
      tap(() => {
        this.handleLogout();
      }),
      catchError((error) => {
        this.handleLogout();
        return throwError(() => error);
      })
    );
  }

  private handleLogout() {
    this._clearSession();
    this.resetCallingUrl();
    this.router.navigateByUrl(this.authConfig.redirectUrl);
  }

  resetCallingUrl() {
    this._callingUrl = null;
  }

  checkSessionValid(): Session | null {
    const session = this.getSession();

    if (!session) {
      return null;
    }
    const expiry = new Date(session.exp * 1000);

    if (!isBefore(new Date(), expiry) || session.requiresMfa) {
      this._clearSession();
      return null;
    }

    return session;
  }

  getToken(): string {
    return localStorage.getItem('token') as string;
  }

  getSession(): Session {
    return JSON.parse(localStorage.getItem('session') as string);
  }

  sessionChangeListener(): Observable<boolean> {
    return this.ngZone.runOutsideAngular(() => {
      return fromEvent<StorageEvent>(window, 'storage').pipe(
        filter((event) => event.key === 'session'),
        map((event) => {
          return !!event.newValue;
        })
      );
    });
  }

  private _getUrl() {
    return `${this.baseUrl}/${this.model}`;
  }

  private _get2faUrl() {
    return `${this.baseUrl}/${this.model}/validate-mfa`;
  }

  private _getResend2faUrl() {
    return `${this.baseUrl}/${this.model}/resend-mfa`;
  }

  private _getForgotPasswordUrl() {
    return `${this.baseUrl}/${this.model}/forgot-password`;
  }

  private _getResetPasswordUrl() {
    return `${this.baseUrl}/${this.model}/reset-password`;
  }

  private _getChangePasswordUrl() {
    return `${this.baseUrl}/${this.model}/change-password`;
  }

  private _createSession(token: string): Session {
    return jwt_decode<Session>(token);
  }

  private _clearSession(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('session');
  }

  private _setToken(token: string) {
    localStorage.setItem('token', token);
  }

  private _setTokenAndSession(token: string) {
    const session = this._createSession(token);
    localStorage.setItem('token', token);
    localStorage.setItem('session', JSON.stringify(session));
  }
}
